import { Handshake, MoreHorizontal, Repeat } from 'lucide-react'
import { useState } from 'react'
import { RichTextView } from '@/components/RichTextView'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { toast } from '@/lib/toast'
import { toastUndo } from '@/lib/toastUndo'
import { CategoryChip } from '../components/CategoryChip'
import { todayIso } from '../dates'
import { addDays, shortDay } from '../record/format'
import { agreementAction, completeOccurrence, deleteAgreement, postponeOccurrence, restoreAgreement, skipOccurrence } from './api'
import { AgreementStatusBadge } from './AgreementStatusBadge'
import { followUpLine, historyText, partyText } from './format'
import type { Agreement } from './types'

/**
 * One agreement (spec-63 E4, design §4.6): title, kind and state; party and owner; the follow-up
 * line; the description (cut after four lines); the last three deadlines; and the actions.
 * "Afgehandeld" ticks the open task off (one path with Mijn taken); on a contact rhythm it opens the
 * composer instead, with "Alleen afvinken" next to it (`onContact`). `readOnly` (a member's agreement
 * on an organisation page) shows it without actions.
 */
export function AgreementCard({
  agreement,
  onChanged,
  onEdit,
  onContact,
  readOnly = false,
  showParty = true,
}: {
  agreement: Agreement
  onChanged: () => void
  onEdit: (agreement: Agreement) => void
  onContact: (agreement: Agreement) => void
  readOnly?: boolean
  showParty?: boolean
}) {
  const [busy, setBusy] = useState(false)
  const [dateOpen, setDateOpen] = useState(false)
  const [date, setDate] = useState(addDays(todayIso(), 7))
  const open = agreement.open_deadline
  const running = agreement.status === 'actief'
  const Icon = agreement.schedule === 'terugkerend' || agreement.schedule === 'contactritme' ? Repeat : Handshake

  async function act(work: () => Promise<unknown>, done?: string): Promise<boolean> {
    setBusy(true)
    try {
      await work()
      if (done) toast.success(done)
      onChanged()
      return true
    } catch (caught) {
      toast.error(caught instanceof Error ? caught.message : 'Dat lukte niet.')
      return false
    } finally {
      setBusy(false)
    }
  }

  async function remove() {
    if (!(await act(() => deleteAgreement(agreement.id)))) return
    toastUndo(`Afspraak '${agreement.title}' verwijderd`, async () => {
      await restoreAgreement(agreement.id)
      onChanged()
    })
  }

  const notYet =
    !open && agreement.next_due_on && agreement.task_from
      ? `Volgende deadline ${shortDay(agreement.next_due_on)}; de taak verschijnt op ${shortDay(agreement.task_from)}`
      : undefined

  return (
    <article className="flex flex-col gap-1.5 rounded-lg border border-border p-3" aria-label={agreement.title}>
      <div className="flex flex-wrap items-center gap-2">
        <Icon className="size-4 text-muted-foreground" aria-hidden />
        <span className="font-semibold">{agreement.title}</span>
        {agreement.category ? <CategoryChip category={agreement.category} /> : null}
        <span className="ml-auto">
          <AgreementStatusBadge agreement={agreement} />
        </span>
      </div>
      <p className="text-sm text-muted-foreground">
        {[showParty ? `Met ${partyText(agreement)}` : null, agreement.person && !agreement.person_is_member ? '(voormalig of geen contactpersoon)' : null, agreement.owner ? `eigenaar ${agreement.owner.name}` : null]
          .filter(Boolean)
          .join(' · ')}
      </p>
      <p className="text-sm">{followUpLine(agreement)}</p>
      {agreement.description_html ? <RichTextView html={agreement.description_html} className="line-clamp-4 text-sm" /> : null}
      {agreement.history.length > 0 ? (
        <p className="text-xs text-muted-foreground">
          Historie: {agreement.history.map(historyText).join(' · ')}
          {agreement.history_count > agreement.history.length ? ` · en ${agreement.history_count - agreement.history.length} eerder` : ''}
        </p>
      ) : null}
      {readOnly ? null : (
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <Button
            type="button"
            size="sm"
            disabled={busy || !running || (!open && agreement.schedule !== 'contactritme')}
            title={notYet}
            onClick={() =>
              agreement.schedule === 'contactritme'
                ? onContact(agreement)
                : open && void act(() => completeOccurrence(open.id), 'Afgehandeld.')
            }
          >
            Afgehandeld
          </Button>
          {agreement.schedule === 'contactritme' && open ? (
            <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={() => void act(() => completeOccurrence(open.id), 'Afgevinkt.')}>
              Alleen afvinken
            </Button>
          ) : null}
          {open?.task_id ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" size="sm" variant="outline" disabled={busy}>
                  Uitstellen
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem onSelect={() => void act(() => postponeOccurrence(open.id, { days: 7 }), 'Uitgesteld.')}>+1 week</DropdownMenuItem>
                <DropdownMenuItem onSelect={() => void act(() => postponeOccurrence(open.id, { days: 14 }), 'Uitgesteld.')}>+2 weken</DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setDateOpen(true)}>Kies datum…</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
          {dateOpen && open ? (
            <span className="flex items-center gap-1">
              <input aria-label="Nieuwe datum" type="date" min={todayIso()} value={date} onChange={(event) => setDate(event.target.value)} className="h-8 rounded-md border border-border px-2 text-sm max-md:text-base" />
              <Button type="button" size="sm" onClick={() => void act(() => postponeOccurrence(open.id, { due_date: date }), 'Uitgesteld.').then(() => setDateOpen(false))}>
                Uitstellen
              </Button>
            </span>
          ) : null}
          <Button type="button" size="sm" variant="ghost" onClick={() => onEdit(agreement)}>
            Bewerken
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" size="sm" variant="ghost" aria-label={`Meer acties voor '${agreement.title}'`}>
                <MoreHorizontal />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {agreement.status === 'actief' ? (
                <DropdownMenuItem onSelect={() => void act(() => agreementAction(agreement.id, 'pause'), 'Afspraak gepauzeerd.')}>Pauzeren</DropdownMenuItem>
              ) : null}
              {agreement.status === 'gepauzeerd' ? (
                <DropdownMenuItem onSelect={() => void act(() => agreementAction(agreement.id, 'resume'), 'Afspraak hervat.')}>Hervatten</DropdownMenuItem>
              ) : null}
              {open ? <DropdownMenuItem onSelect={() => void act(() => skipOccurrence(open.id), 'Deadline overgeslagen.')}>Overslaan</DropdownMenuItem> : null}
              {agreement.status !== 'beeindigd' ? (
                <DropdownMenuItem onSelect={() => void act(() => agreementAction(agreement.id, 'end'), 'Afspraak beëindigd.')}>Beëindigen</DropdownMenuItem>
              ) : null}
              <DropdownMenuItem className="text-destructive" onSelect={() => void remove()}>
                Verwijderen
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </article>
  )
}
